import React, { ReactNode, useMemo } from 'react';
import cl from 'classnames';
import { $TSFixMe } from 'src/interface';
import { useLanguage } from 'src/hooks';
import { StickyTableConfig, STSortOption } from './stickyTable.interface';
import styles from './printTableStyle.module.scss';

type Cell = {
  key: string;
  node: ReactNode;
};

type PrintTableProps<T> = {
  config: StickyTableConfig<T>[];
  data: T[];
  summaryRecord?: T;
  className?: string;
  sortOption?: STSortOption | null;
};

declare const DeclaredPrintTableType: <RecordType extends object = $TSFixMe>(
  props: PrintTableProps<RecordType>,
) => React.ReactElement;

type PrintTableType = typeof DeclaredPrintTableType;

export const PrintTable: PrintTableType = ({
  config,
  data,
  summaryRecord,
  className,
  sortOption = null,
}) => {
  const columnsCount = useMemo(() => config.length, [config.length]);
  const { t } = useLanguage();

  const rows = useMemo(() => {
    const sortFunction = sortOption
      ? config.find(({ key }) => sortOption.key === key)?.sorter
      : null;

    const sortedData = [...data].sort((a, b) => {
      if (!sortOption || !sortFunction) {
        return 0;
      }

      return sortFunction(a, b, sortOption.sortOrder);
    });

    const preparedData: Cell[][] = sortedData.map((record) =>
      config.map(({ key, field, render, printRender }, index) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        const value = record[field];
        return {
          key,
          node:
            printRender?.(value, record, index) ||
            render?.(value, record, index) ||
            value,
        };
      }),
    );

    if (summaryRecord && sortedData.length) {
      preparedData.unshift(
        config.map(
          ({ key, field, summaryRender, printRender, render }, index) => {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            const value = summaryRecord[field];
            return {
              key,
              node:
                summaryRender?.(value, summaryRecord, index) ||
                printRender?.(value, summaryRecord, index) ||
                render?.(value, summaryRecord, index) ||
                value,
            };
          },
        ),
      );
    }

    return preparedData;
  }, [config, data, sortOption, summaryRecord]);

  return (
    <div
      className={cl(styles.table, className)}
      style={{ gridTemplateColumns: `repeat(${columnsCount}, auto)` }}
    >
      {config.map(({ key, title }) => (
        <div key={`pt-h-${key}`} className={styles.headerCell}>
          {title.replaceAll('\n', ' ')}
        </div>
      ))}
      {rows.map((cells, rowIndex) =>
        cells.map(({ key, node }) => (
          <div
            key={`pt-c-row-${rowIndex}-${key}`}
            className={cl(
              styles.cell,
              summaryRecord && rowIndex === 0 && styles.summary,
            )}
          >
            {node}
          </div>
        )),
      )}
      {!rows.length && (
        <div
          className={cl(styles.cell, styles.summary)}
          style={{ gridColumn: `1 / ${columnsCount + 1}` }}
        >
          {t('general.noData')}
        </div>
      )}
    </div>
  );
};

export default PrintTable;
